/**
 * Convert stored GFM markdown (what MessageCompose sends) back into contentEditable HTML
 * so edit mode matches the live composer (inline code, fenced blocks, etc.).
 */
import { marked } from 'marked';
import { sanitizeHtml } from '@utils/sanitizeHtml';

/**
 * Heuristic: message body is probably markdown (same idea as RichTextEditor paste path).
 * @param {string} text
 */
export function looksLikeMarkdown(text) {
    if (!text || typeof text !== 'string') return false;
    const s = text.trim();
    if (s.length < 2) return false;
    return (
        /^#{1,6}\s/m.test(s) ||
        /^ {0,3}[-*+]\s/m.test(s) ||
        /^ {0,3}\d+\.\s/m.test(s) ||
        /\*\*[^*\n]+\*\*/.test(s) ||
        /(^|\s)_[^_`\n]+_(\s|$)/.test(s) ||
        /`[^`\n]+`/.test(s) ||
        /```[\s\S]*```/.test(s) ||
        /\[.+\]\([^)\s]+\)/.test(s) ||
        /^ {0,3}>\s/m.test(s) ||
        /~~[^~]+~~/.test(s) ||
        // Underline / other inline HTML from htmlToMarkdown → edit round-trip
        /<\/?[a-z][a-z0-9]*\b/i.test(s)
    );
}

/**
 * @param {string} md - GFM markdown from chat storage
 * @returns {string} Sanitized HTML safe for innerHTML in the compose editor
 */
export function markdownToComposeHtml(md) {
    if (typeof md !== 'string' || !md.trim()) return '';
    try {
        const raw = marked.parse(md.trim(), { async: false, gfm: true, breaks: false });
        const html = typeof raw === 'string' ? raw : String(raw ?? '');
        let safe = sanitizeHtml(html);
        /** Match MessageCompose `togglePre` / `applyFormatBlockTag('pre')` shell for styling + toolbar. */
        safe = safe.replace(/<pre>/gi, '<pre class="compose-pre">');
        return safe.trim();
    } catch {
        return '';
    }
}

/**
 * Stored chat body → sanitized HTML for the compose editor (markdown, mixed MD+HTML, or HTML-only).
 * @param {string} raw
 * @returns {string} HTML or '' to fall back to plain text
 */
export function storedChatBodyToComposeHtml(raw) {
    const s = String(raw ?? '').trim();
    if (!s) return '';
    if (looksLikeMarkdown(s)) {
        const fromMd = markdownToComposeHtml(s);
        if (fromMd) return fromMd;
    }
    if (/<\/?[a-z][a-z0-9]*\b/i.test(s)) {
        const fromHtml = sanitizeHtml(s);
        if (fromHtml.trim()) return fromHtml.trim();
    }
    return '';
}
